import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} BundleVariant
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {string[]} options
 */

/**
 * @typedef {Object} BundleRefs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement[]} [items]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [totalSale]
 * @property {HTMLElement} [totalCompare]
 */


class BundleAndSave extends Component {
  #abortController = new AbortController();

  /** @type {HTMLElement[]} */
  #rows = [];

  connectedCallback() {
    super.connectedCallback();

    this.discountType = this.dataset.discountType;
    this.tier = parseFloat(this.dataset.tier ?? '') || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.#rows = Array.from(this.querySelectorAll('.bundle-and-save__item'));

    for (const row of this.#rows) {
      const script = row.querySelector('script.bundle-and-save__variant-data');
      try {
        row._variants = /** @type {BundleVariant[]} */ (JSON.parse(script?.textContent || '[]'));
      } catch {
        row._variants = [];
      }
      this.#updateRow(row);
    }

    this.#updateTotals();

    // Close any open .bundle-and-save__dropdown when clicking outside it.
    // Mirrors the click-outside handler in snippets/variant-main-picker.liquid.
    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #handleDocumentClick = (event) => {
    this.querySelectorAll('.bundle-and-save__dropdown[open]').forEach((details) => {
      if (details instanceof HTMLDetailsElement && !details.contains(/** @type {Node} */ (event.target))) {
        details.removeAttribute('open');
      }
    });
  };

  /**
   * Called by `on:change="/handleOptionChange"` on each option radio.
   * @param {Event} event
   */
  handleOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const row = input.closest('.bundle-and-save__item');
    if (row instanceof HTMLElement) this.#updateRow(row);
    this.#updateTotals();
    this.#syncDropdown(input);
  }

  /**
   * If the changed input belongs to a `.bundle-and-save__dropdown`, refresh
   * the trigger text + value-handle, update is-selected state on options,
   * mirror the new value into the label's preview span, then close the
   * <details>. Mirrors the JS handler in snippets/variant-main-picker.liquid.
   * @param {HTMLInputElement} input
   */
  #syncDropdown(input) {
    const dropdown = input.closest('.bundle-and-save__dropdown');
    if (!(dropdown instanceof HTMLDetailsElement)) return;

    const trigger = dropdown.querySelector('.bundle-and-save__dropdown-trigger');
    const triggerText = dropdown.querySelector('[data-bundle-dropdown-trigger-text]');
    const selectedOption = input.closest('.bundle-and-save__dropdown-option');
    const labelValue = dropdown.parentElement?.querySelector('[data-bundle-dropdown-value]');

    if (triggerText) triggerText.textContent = input.value;
    if (labelValue) labelValue.textContent = input.value;

    if (trigger instanceof HTMLElement && selectedOption instanceof HTMLElement && selectedOption.dataset.valueHandle) {
      trigger.dataset.valueHandle = selectedOption.dataset.valueHandle;
    }

    dropdown.querySelectorAll('.bundle-and-save__dropdown-option').forEach((option) => {
      option.classList.toggle('is-selected', option === selectedOption);
    });

    dropdown.removeAttribute('open');
  }

  /**
   * Called by `on:click="/handleAddBundle"` on the CTA.
   * @param {Event} [event]
   */
  async handleAddBundle(event) {
    event?.preventDefault();

    const items = this.#rows
      .map((row) => ({ id: Number(row.dataset.selectedVariantId), quantity: 1 }))
      .filter((item) => Number.isInteger(item.id) && item.id > 0);

    if (items.length === 0) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const { addButton } = this.refs;
    addButton.setAttribute('disabled', '');
    this.#clearError();

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        signal: this.#abortController.signal,
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description));
        this.#showError(data.description || data.message);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'bundle-and-save',
          itemCount: items.length,
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
      this.#showError();
    } finally {
      addButton.removeAttribute('disabled');
    }
  }

  /**
   * Resolve the selected variant for a row, then refresh availability + price.
   * @param {HTMLElement} row
   */
  #updateRow(row) {
    const selected = this.#selectedOptions(row);
    const variant = this.#findVariant(row._variants, selected);

    row.dataset.selectedVariantId = variant ? String(variant.id) : '';
    row.dataset.available = variant && variant.available ? 'true' : 'false';
    if (variant) row.dataset.linePrice = String(variant.price);

    this.#updateImage(row, variant);
    this.#updateAvailability(row, selected);
  }

  /**
   * Swap the row image to the selected variant's featured image, if it has one.
   * @param {HTMLElement} row
   * @param {(BundleVariant & {featured_image?: {src?: string}})|undefined} variant
   */
  #updateImage(row, variant) {
    const src = variant?.featured_image?.src;
    const image = row.querySelector('.bundle-and-save__image');
    if (!src || !(image instanceof HTMLImageElement)) return;

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '308');
      image.src = url.toString();
    } catch {
      image.src = src;
    }
    image.removeAttribute('srcset');
  }

  /**
   * @param {HTMLElement} row
   * @returns {(string|null)[]} selected value name per option position (0-based)
   */
  #selectedOptions(row) {
    /** @type {(string|null)[]} */
    const selected = [];
    row.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        selected[Number(input.dataset.optionPosition) - 1] = input.value;
      }
    });
    return selected;
  }

  /**
   * @param {BundleVariant[]} variants
   * @param {(string|null)[]} selected
   * @returns {BundleVariant|undefined}
   */
  #findVariant(variants, selected) {
    return variants.find((variant) =>
      selected.every((value, index) => value == null || variant.options?.[index] === value)
    );
  }

  /**
   * Toggle the `is-unavailable` state of each option value given the current
   * selection of the other options — mirrors the native variant picker.
   * @param {HTMLElement} row
   * @param {(string|null)[]} selected
   */
  #updateAvailability(row, selected) {
    row.querySelectorAll('.bundle-and-save__option').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;
      const position = Number(group.dataset.optionPosition);

      group.querySelectorAll('.bundle-and-save__value').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        if (!(label instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

        const candidate = selected.slice();
        candidate[position - 1] = input.value;
        const match = this.#findVariant(row._variants, candidate);
        const available = Boolean(match && match.available);

        label.classList.toggle('is-unavailable', !available);
        input.dataset.optionAvailable = String(available);
      });
    });
  }

  /** Recompute per-line and total prices, and the CTA disabled state. */
  #updateTotals() {
    const prices = this.#rows.map((row) => Number(row.dataset.linePrice) || 0);
    const sum = prices.reduce((total, price) => total + price, 0);
    const totalSale = this.#applyDiscount(sum, sum);

    this.#rows.forEach((row, index) => {
      const lineSale = this.#applyDiscount(prices[index], sum);
      this.#setMoney(row.querySelector('[data-line-compare]'), prices[index]);
      this.#setMoney(row.querySelector('[data-line-sale]'), lineSale);
    });

    this.#setMoney(this.querySelector('[data-total-compare]'), sum);
    this.#setMoney(this.querySelector('[data-total-sale]'), totalSale);

    const allAvailable = this.#rows.every((row) => row.dataset.available === 'true');
    this.refs.addButton.toggleAttribute('disabled', !allAvailable);
  }

  /**
   * @param {number} amount - line or sum in minor units
   * @param {number} sum - bundle sum in minor units (for fixed-amount proration)
   * @returns {number}
   */
  #applyDiscount(amount, sum) {
    if (this.tier <= 0 || sum <= 0) return amount;

    if (this.discountType === 'Fixed amount') {
      const discountCents = this.tier * 100;
      const share = amount / sum;
      return Math.max(0, Math.round(amount - discountCents * share));
    }
    // Percentage
    return Math.round((amount * (100 - this.tier)) / 100);
  }

  /**
   * @param {Element|null} node
   * @param {number} cents
   */
  #setMoney(node, cents) {
    if (node) node.textContent = formatMoney(cents, this.moneyFormat, this.currency);
  }

  /** @param {string} [message] */
  #showError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message || Theme.translations?.cart_error || '';
    error.hidden = false;
  }

  #clearError() {
    const { error } = this.refs;
    if (error) error.hidden = true;
  }
}

if (!customElements.get('bundle-and-save-component')) {
  customElements.define('bundle-and-save-component', BundleAndSave);
}
